import { useParams, Link, Navigate } from "react-router-dom";
import { useContent } from "../contexts/ContentContext";
import { Scale, FileText, ShoppingCart, ShieldCheck } from "lucide-react";

const PAGES = {
  "mentions-legales": { key: "legal.mentions", title: "Informations légales", Icon: Scale },
  "conditions-utilisation": { key: "legal.terms", title: "Conditions d'utilisation", Icon: FileText },
  "conditions-vente": { key: "legal.sales", title: "Conditions de vente", Icon: ShoppingCart },
  confidentialite: { key: "legal.privacy", title: "Politique de confidentialité", Icon: ShieldCheck },
};

/** Rendu simple : lignes "## " = titre de section, "- " = puce, autres = paragraphes. */
function renderLegal(text) {
  const blocks = [];
  let list = null;
  const flushList = () => { if (list) { blocks.push({ type: "ul", items: list }); list = null; } };
  for (const raw of (text || "").split("\n")) {
    const line = raw.trim();
    if (!line) { flushList(); continue; }
    if (line.startsWith("## ")) { flushList(); blocks.push({ type: "h2", text: line.slice(3) }); }
    else if (line.startsWith("- ")) { (list = list || []).push(line.slice(2)); }
    else { flushList(); blocks.push({ type: "p", text: line }); }
  }
  flushList();
  return blocks;
}

export default function Legal() {
  const { slug } = useParams();
  const { t } = useContent();
  const page = PAGES[slug];
  if (!page) return <Navigate to="/legal/mentions-legales" replace />;
  const blocks = renderLegal(t(page.key, ""));
  const updated = new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long" });

  return (
    <div className="container-mc py-12 max-w-3xl" data-testid="legal-page">
      <nav className="flex flex-wrap gap-2 mb-8" data-testid="legal-nav">
        {Object.entries(PAGES).map(([s, p]) => (
          <Link key={s} to={`/legal/${s}`} data-testid={`legal-tab-${s}`}
            className={`text-xs font-mono uppercase tracking-wider px-3 py-2 border rounded-sm transition-colors ${s === slug ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}>
            {p.title}
          </Link>
        ))}
      </nav>

      <div className="flex items-center gap-3 mb-2">
        <page.Icon className="w-7 h-7 text-[hsl(var(--primary))]" />
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter" data-testid="legal-title">{page.title}</h1>
      </div>
      <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-8">Booking Artistes 224 · MCZ EVENTS · Dernière mise à jour : {updated}</p>

      <div className="space-y-4" data-testid="legal-content">
        {blocks.map((b, i) =>
          b.type === "h2" ? (
            <h2 key={i} className="font-display font-bold text-lg mt-8 first:mt-0">{b.text}</h2>
          ) : b.type === "ul" ? (
            <ul key={i} className="list-disc pl-5 space-y-1.5 text-sm text-muted-foreground leading-relaxed">
              {b.items.map((it, j) => <li key={j}>{it}</li>)}
            </ul>
          ) : (
            <p key={i} className="text-sm text-muted-foreground leading-relaxed">{b.text}</p>
          )
        )}
      </div>

      <div className="card-mc p-5 mt-12 bg-[hsl(var(--muted))]/40">
        <p className="text-sm">Une question sur ces documents ? Écrivez-nous : <a href="mailto:contact@bookingartistes224.com" className="font-bold underline">contact@bookingartistes224.com</a></p>
      </div>
    </div>
  );
}
